<template>
    <slot></slot>
</template>

<script lang="ts" setup>
import Dropzone from 'dropzone';
import axios from "axios";
import {inject, onMounted} from "vue";
import type {Dictionary} from "../types.ts";
import type Provider from "../provider.ts";

interface Props {
    token: string
    url: string
    content: Dictionary<any>
    config: Dictionary<any>
    slug: string
    tag?: string
}

const {token, url, content, config, slug, tag} = defineProps<Props>();

const {
    semaphore,
    dropzoneErrorState
} = (inject('Provider') as Provider).refs;

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

let uploadCount = 0;

onMounted(() => {
    const previewElement = document.querySelector('#preview-template');
    const submitButtonElement = document.querySelector('#form-long button[type="submit"]') as HTMLButtonElement;

    new Dropzone('.dropzone', {
        url: url + slug + (tag ? '/' + tag : ''),
        headers: {
            'X-CSRF-TOKEN': token
        },

        previewTemplate: previewElement?.innerHTML || '',
        createImageThumbnails: true,
        uploadMultiple: true,
        parallelUploads: 2,
        maxFiles: config.dropzone.maxFiles,
        timeout: 0,
        maxFilesize: config.dropzone.maxFilesize,
        acceptedFiles: config.dropzone.acceptedFiles,
        addRemoveLinks: true,

        dictRemoveFile: "<i class='fas fa-fw fa-trash ml-3 text-body'></i>",
        dictInvalidFileType: 'Dateityp nicht erlaubt, nur PDF, DOC und DOCX',
        dictFileTooBig: 'Datei zu Groß (@{{filesize}}MB), Maximalgröße @{{maxFilesize}}MB',
        dictResponseError: "Server antwortet mit Code @{{statusCode}}",
        dictCancelUploadConfirmation: "Upload wirklich abbrechen?",
        dictMaxFilesExceeded: "Keine weiteren Dateien möglich.",
        dictCancelUpload: "<i class='fas fa-fw fa-times ml-3 text-danger'></i>",

        init: function () {
            const self = this;

            this.messageElement = this.element.querySelector('.dz-message');
            this.messageDisabledElement = this.element.querySelector('.dz-disabled-message');

            this.on('success', function () {
                uploadCount++;

                if (self.options.maxFiles && self.options.maxFiles > 0) {
                    if (uploadCount >= self.options.maxFiles) {
                        self.messageElement.style.display = "none";
                        self.messageDisabledElement.style.display = "block";
                    }
                }
            });

            this.on('error', function () {
                dropzoneErrorState.hasUploadError = self.files.some(f => f.status === Dropzone.ERROR)
            });

            this.on('removedfile', function (file: any) {
                dropzoneErrorState.hasUploadError = self.files.some(f => f.status === Dropzone.ERROR);

                axios.delete(url + slug, {
                    data: {
                        filename: file.name
                    },
                    headers: self.options.headers
                })
                    .then(() => {
                        uploadCount = Math.max(0, uploadCount--);

                        if (self.options.maxFiles) {
                            if (uploadCount < self.options.maxFiles) {
                                self.messageElement.style.display = "block";
                                self.messageDisabledElement.style.display = "none";
                            }
                        }
                    })
                    .catch(() => {});
            });

            // hide error message
            this.on('maxfilesexceeded', function (file: any) {
                if (file.previewElement) {
                    file.previewElement.style.display = "none";
                }
            });

            this.on("addedfile", function () {
                semaphore.value++;
            });

            this.on("complete", function () {
                semaphore.value--;
            })
        }
    });
})

</script>
